import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function PasswordGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/verify-password`, { password });
      if (response.data.success) {
        onSuccess();
      } else {
        setError("Incorrect password. Please try again.");
        setPassword("");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#FDFBF7" }}
      data-testid="password-gate"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="w-full max-w-sm text-center"
      >
        <div className="mb-10">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ background: "#E6E2DD" }}
          >
            <Lock className="w-6 h-6" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
          </div>
          <h1
            className="text-3xl md:text-4xl mb-3"
            style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
          >
            Private Profile
          </h1>
          <p
            className="text-sm tracking-wide"
            style={{ fontFamily: "'Manrope', sans-serif", color: "#999999" }}
          >
            This profile is shared with warmth and sincerity.
            <br />
            Please enter the password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="h-12 text-center text-base rounded-lg border"
            style={{
              background: "#FFFFFF",
              borderColor: error ? "#c98a7a" : "#E6E2DD",
              color: "#2D2D2A",
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="password-input"
            autoFocus
          />

          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-sm"
                style={{ color: "#c98a7a" }}
                data-testid="password-error"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <Button
            type="submit"
            disabled={loading || !password.trim()}
            className="w-full h-12 rounded-lg text-base font-medium transition-all duration-300"
            style={{
              background: "#8A9A5B",
              color: "#FDFBF7",
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="password-submit"
          >
            {loading ? (
              <span className="animate-pulse">Verifying...</span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Enter <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </form>

        <p
          className="mt-8 text-xs tracking-wide"
          style={{ color: "#C4B298", fontFamily: "'Manrope', sans-serif" }}
        >
          Accessible via direct link only
        </p>
      </motion.div>
    </div>
  );
}
